// @ts-strict-ignore
import React, { useCallback, useState } from 'react'
import { useAtom, useSetAtom } from 'jotai'
import { useTranslation } from 'react-i18next'
import { Box, Upload } from '@node-space/ui-components'
import { useMappedRoles } from 'hooks/useMappedRoles'
import { FILE_UPLOAD_LIMIT } from 'utils/documents'
import { logSentryError } from 'utils/sentry'
import { fileAtom, fileNameAtom, uploadErrorAtom } from '../../atoms/massPayoutAtoms'

export const MassPayoutFileUpload = () => {
  const { t } = useTranslation()
  const [error, setError] = useState('')
  const [file, setFile] = useAtom(fileAtom)
  const permissions = useMappedRoles()
  const canEdit = permissions?.massPayouts?.canEdit
  const setUploadError = useSetAtom(uploadErrorAtom)
  const setFileName = useSetAtom(fileNameAtom)

  const onFileChange = useCallback(
    (acceptedFile: File | null) => {
      setError('')
      setUploadError('')

      if (!acceptedFile) {
        setFile(null)
        setFileName('')
        return
      }

      try {
        // -- error: file exceeds max size
        if (acceptedFile?.size > FILE_UPLOAD_LIMIT) {
          setError(t('documents.smallerFileRequired'))
          return
        }

        // -- read file data and set state
        const reader = new FileReader()

        // -- to handle other events on the reader, use 'reader.onabort' and 'reader.onerror'
        reader.onload = () => {
          const dataUrl = reader?.result as string

          const fileData = { fileData: acceptedFile, buffer: dataUrl }

          setFileName(acceptedFile?.name)
          setFile(fileData)
        }

        reader.readAsDataURL(acceptedFile)
      } catch (error) {
        console.error('error', error)
        logSentryError('Masspayouts crypto onFileChange', error)
        setError(t('oopsSomethingWentWrong'))
      }
    },
    [setError, setUploadError, setFile, setFileName]
  )

  return (
    <Box flex flexCol gapY={4}>
      <Upload
        full
        onFileChange={onFileChange}
        textDictionary={{
          defaultText: t('massPayouts.csvLessThan10MB'),
          dragAndDrop: t('documents.dropFile'),
          uploadText: t('massPayouts.upload'),
          replaceText: t('massPayouts.orDragAndDropToReplace'),
        }}
        errorMessage={error}
        label={t('massPayouts.uploadCSV')}
        accept={{ 'text/csv': ['.csv'] }}
        testId="mass-payout-upload"
      />

      <p className="text-foreground-prominent text-sm">
        {t('massPayouts.weAdvisePayingInBatch100')}
      </p>
    </Box>
  )
}
